import React, { useState, useRef } from 'react';
import { FiBook, FiChevronDown, FiChevronRight, FiSearch, FiX } from 'react-icons/fi';
import '../../../assets/css/documentation.css';

const Documentation = () => {
    const [expandedSections, setExpandedSections] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const sectionRefs = useRef({});

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const scrollToSection = (sectionId) => {
        // Expand the section first
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: true
        }));
        
        // Scroll to section after a small delay to ensure it's expanded
        setTimeout(() => {
            const element = sectionRefs.current[sectionId];
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // Highlight the section briefly
                element.classList.add('highlight-section');
                setTimeout(() => {
                    element.classList.remove('highlight-section');
                }, 2000);
            }
        }, 100);
    };

    const sections = [
        { id: 'introduction', title: 'Introduction', keywords: ['introduction', 'overview', 'key features', 'platform', 'content creation'] },
        { id: 'gettingStarted', title: 'Getting Started', keywords: ['login', 'dashboard', 'getting started', 'authentication'] },
        { id: 'roles', title: 'User Roles & Permissions', keywords: ['roles', 'permissions', 'admin', 'custom roles', 'access'] },
        { id: 'manualContent', title: 'Creating Content Manually', keywords: ['create content', 'manual', 'email', 'sms', 'popup', 'push notification', 'fields'] },
        { id: 'aiAssistant', title: 'AI Assistant', keywords: ['ai assistant', 'ai', 'generate', 'content generator', 'gpt', '6-step', 'campaign'] },
        { id: 'library', title: 'Content Library', keywords: ['library', 'filters', 'search', 'sorting', 'view content'] },
        { id: 'bulkActions', title: 'Bulk Actions', keywords: ['bulk', 'bulk actions', 'delete multiple', 'duplicate', 'status change', 'mass operations'] },
        { id: 'analytics', title: 'Analytics', keywords: ['analytics', 'statistics', 'reports', 'dashboard', 'metrics', 'export', 'charts'] },
        { id: 'editing', title: 'Editing Content', keywords: ['edit', 'version control', 'version history', 'multi-language', 'comments'] },
        { id: 'translation', title: 'AI Translation', keywords: ['translation', 'translate', 'languages', 'ai translation'] },
        { id: 'statuses', title: 'Status Management', keywords: ['status', 'workflow', 'status management', 'enable', 'disable'] },
        { id: 'emailTemplates', title: 'Email Templates', keywords: ['email templates', 'template builder', 'html', 'blocks', 'custom block', 'my blocks', 'tr', 'drag and drop', 'brand preview'] },
        { id: 'integrations', title: 'CRM Integrations (Optimove)', keywords: ['integrations', 'crm', 'optimove', 'optimail', 'api key', 'push', 'export', 'template', 'brand', 'folder', 'all languages'] },
        { id: 'placeholders', title: 'Placeholders', keywords: ['placeholders', 'dynamic content', 'personalization', 'variables', 'merge fields'] },
        { id: 'users', title: 'User Management', keywords: ['users', 'user management', 'create user', 'delete user'] },
        { id: 'brands', title: 'Brand Management', keywords: ['brands', 'brand configuration', 'logo', 'colors'] },
        { id: 'languages', title: 'Language Management', keywords: ['languages', 'language management', 'active', 'inactive'] },
        { id: 'profile', title: 'Profile Settings', keywords: ['profile', 'password', 'change password', 'edit names'] },
        { id: 'darkMode', title: 'Dark Mode', keywords: ['dark mode', 'theme', 'light', 'dark'] },
        { id: 'bestPractices', title: 'Best Practices', keywords: ['best practices', 'guidelines', 'tips', 'recommendations'] },
        { id: 'troubleshooting', title: 'Troubleshooting', keywords: ['troubleshooting', 'problems', 'issues', 'help', 'cannot'] }
    ];

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        
        if (term.length > 0) {
            // Search in sections
            const results = sections.filter(section => {
                const titleMatch = section.title.toLowerCase().includes(term);
                const keywordMatch = section.keywords.some(keyword => keyword.includes(term));
                return titleMatch || keywordMatch;
            }).map(section => ({
                ...section,
                preview: section.title
            }));
            
            setSearchResults(results);
        } else {
            setSearchResults([]);
        }
    };

    const clearSearch = () => {
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleResultClick = (sectionId) => {
        scrollToSection(sectionId);
        setSearchResults([]);
        setSearchTerm('');
    };

    return (
        <div className="documentation-container">
            <div className="documentation-header">
                <FiBook size={32} />
                <h1>Content Craft Documentation</h1>
                <p>Complete guide to using Content Craft for content creation and management</p>
                
                {/* Search Bar */}
                <div className="doc-search-container">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        className="doc-search-input"
                        placeholder="Search documentation..."
                        value={searchTerm}
                        onChange={handleSearch}
                    />
                    {searchTerm && (
                        <button className="search-clear-btn" onClick={clearSearch}>
                            <FiX />
                        </button>
                    )}
                    
                    {/* Search Results Dropdown */}
                    {searchResults.length > 0 && (
                        <div className="search-results-dropdown">
                            {searchResults.map(result => (
                                <div 
                                    key={result.id}
                                    className="search-result-item"
                                    onClick={() => handleResultClick(result.id)}
                                >
                                    <div className="result-title">{result.title}</div>
                                    <div className="result-preview">Click to jump to this section</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Introduction */}
            <div className="doc-section" ref={el => sectionRefs.current['introduction'] = el}>
                <div className="section-title" onClick={() => toggleSection('introduction')} data-section="introduction">
                    {expandedSections.introduction ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Introduction</h2>
                </div>
                {expandedSections.introduction && (
                    <div className="section-content">
                        <p>Content Craft is a comprehensive content creation and management platform designed for marketing teams. It supports multiple content types including Email, SMS, Push Notifications, Popups, and Free Text, with AI-powered assistance for content generation and translation.</p>
                        
                        <h3>Key Features</h3>
                        <ul>
                            <li><strong>Multi-Format Content Creation:</strong> Create Email, SMS, Push Notifications, Popups, and Free Text content</li>
                            <li><strong>AI Assistant:</strong> Generate content using GPT-4 with customizable campaign types and tones</li>
                            <li><strong>Multi-Language Support:</strong> Create and translate content across multiple languages</li>
                            <li><strong>Role-Based Permissions:</strong> Custom roles with granular permission control</li>
                            <li><strong>Version Control:</strong> Track all changes with comprehensive version history</li>
                            <li><strong>Status Workflows:</strong> Customizable status system for content approval</li>
                            <li><strong>Brand Management:</strong> Configure brand-specific settings and styling</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Getting Started */}
            <div className="doc-section" ref={el => sectionRefs.current['gettingStarted'] = el}>
                <div className="section-title" onClick={() => toggleSection('gettingStarted')} data-section="gettingStarted">
                    {expandedSections.gettingStarted ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Getting Started</h2>
                </div>
                {expandedSections.gettingStarted && (
                    <div className="section-content">
                        <h3>Login</h3>
                        <p>Access the system using your username and password provided by your administrator.</p>

                        <h3>Dashboard</h3>
                        <p>After logging in, you'll see the customizable dashboard with:</p>
                        <ul>
                            <li><strong>Status Overview:</strong> View content distribution across different statuses (if status system is enabled)</li>
                            <li><strong>Recent Activity:</strong> See latest content updates and changes</li>
                            <li><strong>Quick Stats:</strong> Overview of total creatives, languages, and brands</li>
                            <li><strong>Creative Types:</strong> Quick access to create different content types</li>
                        </ul>
                        <p><strong>Dashboard Customization:</strong> Click the settings icon to show/hide sections, reorder them, and configure which cards appear in Quick Stats.</p>
                    </div>
                )}
            </div>

            {/* User Roles & Permissions */}
            <div className="doc-section" ref={el => sectionRefs.current['roles'] = el}>
                <div className="section-title" onClick={() => toggleSection('roles')} data-section="roles">
                    {expandedSections.roles ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>User Roles & Permissions</h2>
                </div>
                {expandedSections.roles && (
                    <div className="section-content">
                        <h3>Role System</h3>
                        <p>Content Craft uses a flexible role-based permission system. There is one default <strong>Admin</strong> role with full system access, and you can create custom roles with specific permissions.</p>
                        
                        <h3>Default Admin Role</h3>
                        <p>The Admin role has all permissions enabled by default and cannot be deleted (system role). Users with Admin role have:</p>
                        <ul>
                            <li>Full access to all content types (create, edit, delete, view)</li>
                            <li>User and role management capabilities</li>
                            <li>Access to all system settings and configurations</li>
                            <li>AI Assistant and translation features</li>
                            <li>Brand and language management</li>
                        </ul>

                        <h3>Creating Custom Roles</h3>
                        <p>Navigate to <strong>Roles Management</strong> to create custom roles. For each role, you can configure:</p>
                        
                        <h4>Available Permissions:</h4>
                        <ul>
                            <li><strong>Dashboard:</strong> View dashboard (enabled by default for all roles)</li>
                            <li><strong>SMS Permissions:</strong> Create, Edit, Delete, View, AI Translate SMS</li>
                            <li><strong>Email Permissions:</strong> Create, Edit, Delete, View, AI Translate Email</li>
                            <li><strong>Email Templates:</strong> Create, Edit, Delete email templates</li>
                            <li><strong>Popup Permissions:</strong> Create, Edit, Delete, View, AI Translate Popup</li>
                            <li><strong>Push Notification Permissions:</strong> Create, Edit, Delete, View, AI Translate Push Notifications</li>
                            <li><strong>Library:</strong> View content library</li>
                            <li><strong>History:</strong> View and restore version history</li>
                            <li><strong>Export/Extract:</strong> Copy HTML code and extract content</li>
                            <li><strong>Languages:</strong> Manage system languages</li>
                            <li><strong>Brands:</strong> Manage and delete brands</li>
                            <li><strong>AI Assistant:</strong> Use AI-powered content generation</li>
                            <li><strong>CRM Integrations:</strong> "Manage Integrations" (connect and configure CRMs such as Optimove) and "Push Templates to CRM" (send email creatives to the active CRM)</li>
                            <li><strong>Admin Functions:</strong> Manage roles and users (Admin only)</li>
                        </ul>

                        <p><strong>Note:</strong> The Admin role is a system role and cannot be deleted. Custom roles can be deleted if no users are assigned to them.</p>
                    </div>
                )}
            </div>

            {/* Creating Content Manually */}
            <div className="doc-section" ref={el => sectionRefs.current['manualContent'] = el}>
                <div className="section-title" onClick={() => toggleSection('manualContent')} data-section="manualContent">
                    {expandedSections.manualContent ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Creating Content Manually</h2>
                </div>
                {expandedSections.manualContent && (
                    <div className="section-content">
                        <h3>Content Types</h3>
                        <p>Content Craft supports five content types. Use the dashboard or navigation menu to create new content.</p>

                        <h3>Creation Modes</h3>
                        <p>When creating content, you can choose between two modes:</p>
                        <ul>
                            <li><strong>Translate Mode:</strong> Create content in one language and automatically translate to selected languages using AI (requires AI Translation permission)</li>
                            <li><strong>Manual Mode:</strong> Manually enter content for each selected language</li>
                        </ul>

                        <h3>Email Fields</h3>
                        <table className="field-table">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Description</th>
                                    <th>Required</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Content Name</td>
                                    <td>Internal name to identify the email in the library</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Campaign Type</td>
                                    <td>Type of campaign (Marketing, System, Transactional, Newsletter, Promotional, Seasonal, etc.)</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Headline</td>
                                    <td>Main headline displayed in the email</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td>Subject Line 1-3</td>
                                    <td>Email subject lines (3 variations for A/B testing)</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td>Pre-header</td>
                                    <td>Preview text shown in email clients (recommended 40-90 characters)</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td>Body</td>
                                    <td>Main email content with HTML formatting support</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Button Name</td>
                                    <td>Text for call-to-action button</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td>Languages</td>
                                    <td>Select one or more languages for the content</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Status</td>
                                    <td>Content workflow status (if status system is enabled)</td>
                                    <td>Conditional</td>
                                </tr>
                            </tbody>
                        </table>

                        <h3>SMS Fields</h3>
                        <table className="field-table">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Description</th>
                                    <th>Required</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Content Name</td>
                                    <td>Internal name to identify the SMS</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Campaign Type</td>
                                    <td>Type of SMS campaign</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Body</td>
                                    <td>SMS message text (recommended max 160 characters for single SMS)</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Languages</td>
                                    <td>Select languages for SMS content</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Status</td>
                                    <td>Content workflow status (if enabled)</td>
                                    <td>Conditional</td>
                                </tr>
                            </tbody>
                        </table>
                        <p><strong>SMS Character Counter:</strong> Shows total characters, SMS parts count, GSM vs Unicode encoding, and characters until next SMS part.</p>

                        <h3>Push Notification Fields</h3>
                        <table className="field-table">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Description</th>
                                    <th>Required</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Content Name</td>
                                    <td>Internal name to identify the notification</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Campaign Type</td>
                                    <td>Type of push notification campaign</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Headline/Title</td>
                                    <td>Notification title (recommended max 65 characters)</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Body</td>
                                    <td>Notification message (recommended max 240 characters)</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Button Name</td>
                                    <td>Action button text</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td>Languages</td>
                                    <td>Select languages for notification</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Status</td>
                                    <td>Content workflow status (if enabled)</td>
                                    <td>Conditional</td>
                                </tr>
                            </tbody>
                        </table>

                        <h3>Popup Fields</h3>
                        <table className="field-table">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Description</th>
                                    <th>Required</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Content Name</td>
                                    <td>Internal name to identify the popup</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Campaign Type</td>
                                    <td>Type of popup campaign</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Headline</td>
                                    <td>Popup headline (recommended max 50 characters)</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Body</td>
                                    <td>Popup message content (recommended 1-4 sentences for AI-generated content)</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Button Name</td>
                                    <td>Call-to-action button text</td>
                                    <td>No</td>
                                </tr>
                                <tr>
                                    <td>Languages</td>
                                    <td>Select languages for popup</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Status</td>
                                    <td>Content workflow status (if enabled)</td>
                                    <td>Conditional</td>
                                </tr>
                            </tbody>
                        </table>

                        <h3>Free Text Fields</h3>
                        <table className="field-table">
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>Description</th>
                                    <th>Required</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Content Name</td>
                                    <td>Internal name to identify the content</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Campaign Type</td>
                                    <td>Type of content campaign</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Body</td>
                                    <td>Free-form text content with HTML support</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Languages</td>
                                    <td>Select languages for content</td>
                                    <td>Yes</td>
                                </tr>
                                <tr>
                                    <td>Status</td>
                                    <td>Content workflow status (if enabled)</td>
                                    <td>Conditional</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* AI Assistant */}
            <div className="doc-section" ref={el => sectionRefs.current['aiAssistant'] = el}>
                <div className="section-title" onClick={() => toggleSection('aiAssistant')} data-section="aiAssistant">
                    {expandedSections.aiAssistant ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>AI Assistant (Content Generator)</h2>
                </div>
                {expandedSections.aiAssistant && (
                    <div className="section-content">
                        <h3>Overview</h3>
                        <p>The AI Assistant uses advanced AI technology to generate professional content across multiple formats. It follows a 6-step wizard process to create tailored content based on your specifications.</p>
                        
                        <p><strong>Permission Required:</strong> Users need the "Use AI Assistant" permission to access this feature.</p>

                        <h3>6-Step Process</h3>
                        
                        <h4>Step 1: Select Campaign Type</h4>
                        <p>Choose from predefined campaign types:</p>
                        <ul>
                            <li>Marketing - General marketing campaigns</li>
                            <li>System - System notifications and alerts</li>
                            <li>Transactional - Order confirmations, receipts, etc.</li>
                            <li>Newsletter - Regular newsletters and updates</li>
                            <li>Promotional - Special offers and promotions</li>
                            <li>Seasonal - Holiday and seasonal campaigns</li>
                            <li>Event - Event announcements and invitations</li>
                            <li>Educational - Tutorials and educational content</li>
                            <li>Survey - Feedback and survey requests</li>
                            <li>Welcome - Onboarding and welcome messages</li>
                        </ul>

                        <h4>Step 2: Select Creative Formats</h4>
                        <p>Choose one or more content formats to generate simultaneously:</p>
                        <ul>
                            <li><strong>Email:</strong> Generates body, headline, 3 subject lines, and pre-header</li>
                            <li><strong>SMS:</strong> Creates concise message (max 160 characters recommended)</li>
                            <li><strong>Push Notification:</strong> Generates title and message body</li>
                            <li><strong>Popup:</strong> Creates headline and short body text (1-4 sentences)</li>
                        </ul>

                        <h4>Step 3: Content Description</h4>
                        <p>Describe what you want to create. The placeholder text provides guidance based on selected formats, including target audience, key message, call-to-action, etc.</p>

                        <h4>Step 4: Select Tone</h4>
                        <p>Choose the content tone:</p>
                        <ul>
                            <li><strong>Normal:</strong> Standard, neutral tone</li>
                            <li><strong>Aggressive:</strong> Bold, urgent messaging</li>
                            <li><strong>Calm:</strong> Gentle, reassuring tone</li>
                            <li><strong>Excited:</strong> Energetic, enthusiastic</li>
                            <li><strong>Professional:</strong> Formal, business-like</li>
                        </ul>

                        <h4>Step 5: Brief Description</h4>
                        <p>Provide detailed context and requirements. Be specific about:</p>
                        <ul>
                            <li>Target audience demographics and preferences</li>
                            <li>Key value propositions and benefits</li>
                            <li>Specific calls-to-action</li>
                            <li>Any must-include information or restrictions</li>
                        </ul>

                        <h4>Step 6: Review & Approve</h4>
                        <p>AI generates content for each selected format. You can:</p>
                        <ul>
                            <li><strong>Like (👍):</strong> Approve the generated content</li>
                            <li><strong>Dislike (👎):</strong> Regenerate new content for that format</li>
                            <li><strong>Edit:</strong> Manually modify any generated text</li>
                        </ul>
                        <p>The system shows character/word count and SMS statistics where applicable.</p>

                        <h3>Creating Content</h3>
                        <p>After approving content:</p>
                        <ol>
                            <li>Enter a <strong>Content Name</strong> to identify the content in your library</li>
                            <li>Select one or more <strong>Languages</strong> - AI will translate to selected languages</li>
                            <li>Click <strong>"Create X Content(s) & Go to Library"</strong> (only appears when name and languages are selected)</li>
                            <li>Content is created and you're redirected to the Library</li>
                        </ol>

                        <h3>AI Generation Details</h3>
                        <ul>
                            <li><strong>Email:</strong> Generates HTML-formatted body with &lt;p&gt;, &lt;strong&gt;, &lt;br&gt; tags for proper structure</li>
                            <li><strong>SMS:</strong> Plain text only, no HTML or special characters</li>
                            <li><strong>Push Notification:</strong> Plain text, max 240 characters recommended</li>
                            <li><strong>Popup:</strong> HTML-formatted, limited to 1-4 sentences for conciseness</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Library */}
            <div className="doc-section" ref={el => sectionRefs.current['library'] = el}>
                <div className="section-title" onClick={() => toggleSection('library')} data-section="library">
                    {expandedSections.library ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Content Library</h2>
                </div>
                {expandedSections.library && (
                    <div className="section-content">
                        <h3>Overview</h3>
                        <p>The Library displays all created content with powerful filtering, sorting, and search capabilities.</p>

                        <h3>Filters</h3>
                        <p>Filter content by:</p>
                        <ul>
                            <li><strong>Language:</strong> Filter by content language (shows languages from actual content)</li>
                            <li><strong>Creative Type:</strong> Filter by campaign type (Marketing, System, etc.)</li>
                            <li><strong>Type:</strong> Filter by content format (Email, SMS, Push-Notification, Popup, Free-text)</li>
                            <li><strong>Created By:</strong> Filter by content creator (shows usernames from actual creatives, including deleted users)</li>
                            <li><strong>Last Updated By:</strong> Filter by last editor</li>
                            <li><strong>Status:</strong> Filter by workflow status (if status system is enabled)</li>
                        </ul>
                        <p><strong>Note:</strong> Created By and Last Updated By filters show only users who have actually created content, not all users in the system. If a user has been deleted, their username still appears for content they created.</p>

                        <h3>Search</h3>
                        <p>Use the search bar to find content by name or other text fields.</p>

                        <h3>Sorting</h3>
                        <p>Sort content by:</p>
                        <ul>
                            <li>Content Name</li>
                            <li>Creative Type (Campaign Type)</li>
                            <li>Type (Content Format)</li>
                            <li>Created Date</li>
                            <li>Updated Date</li>
                            <li>Created By</li>
                            <li>Last Updated By</li>
                            <li>Status (if enabled)</li>
                        </ul>

                        <h3>Content Actions</h3>
                        <p>For each content item, you can:</p>
                        <ul>
                            <li><strong>View:</strong> See full content details in read-only mode</li>
                            <li><strong>Edit:</strong> Modify content (requires edit permission for that content type)</li>
                            <li><strong>Duplicate:</strong> Create a copy of the content</li>
                            <li><strong>Delete:</strong> Remove content (requires delete permission)</li>
                            <li><strong>Version History:</strong> View all versions and changes</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Bulk Actions */}
            <div className="doc-section" ref={el => sectionRefs.current['bulkActions'] = el}>
                <div className="section-title" onClick={() => toggleSection('bulkActions')} data-section="bulkActions">
                    {expandedSections.bulkActions ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Bulk Actions</h2>
                </div>
                {expandedSections.bulkActions && (
                    <div className="section-content">
                        <h3>Overview</h3>
                        <p>Bulk Actions allow you to perform operations on multiple creatives at once, saving time and improving efficiency.</p>

                        <h3>Selecting Creatives</h3>
                        <p>In the Library:</p>
                        <ul>
                            <li>Use the checkbox in the table header to select all creatives on the current page</li>
                            <li>Use individual checkboxes to select specific creatives</li>
                            <li>Selected count is displayed in the Bulk Actions toolbar</li>
                            <li>Selection is cleared when you navigate away or refresh</li>
                        </ul>

                        <h3>Available Bulk Operations</h3>
                        
                        <h4>1. Bulk Delete</h4>
                        <ul>
                            <li><strong>Permission Required:</strong> canBulkDelete + individual delete permissions per content type</li>
                            <li>Deletes all selected creatives and their versions</li>
                            <li>Confirmation modal shows the number of creatives to be deleted</li>
                            <li><strong>Warning:</strong> This action cannot be undone</li>
                        </ul>

                        <h4>2. Bulk Duplicate</h4>
                        <ul>
                            <li><strong>Permission Required:</strong> canBulkDuplicate</li>
                            <li>Creates copies of all selected creatives</li>
                            <li>Duplicates include all languages and versions</li>
                            <li>New copies are named with " - Copy" suffix</li>
                            <li>Creator is set to the user performing the duplication</li>
                        </ul>

                        <h4>3. Bulk Change Status</h4>
                        <ul>
                            <li><strong>Permission Required:</strong> canBulkEdit + edit permissions per content type</li>
                            <li><strong>Available When:</strong> Status system is enabled</li>
                            <li>Changes status for all selected creatives</li>
                            <li>Select new status from dropdown in modal</li>
                            <li>Updates "Last Updated By" field</li>
                        </ul>

                        <h3>Best Practices</h3>
                        <ul>
                            <li>Review your selection carefully before performing bulk operations</li>
                            <li>Start with a small batch to test bulk operations</li>
                            <li>Use filters to narrow down creatives before selecting</li>
                            <li>Bulk delete is permanent - consider duplicating first if unsure</li>
                            <li>Check permissions - bulk operations require both bulk permission AND individual content type permissions</li>
                        </ul>

                        <h3>Permissions</h3>
                        <p>Bulk Actions require specific permissions in your role:</p>
                        <ul>
                            <li><strong>canBulkDelete:</strong> Required for bulk deletion</li>
                            <li><strong>canBulkEdit:</strong> Required for bulk status changes</li>
                            <li><strong>canBulkDuplicate:</strong> Required for bulk duplication</li>
                            <li>Additionally, you must have the appropriate individual permissions (canDeleteSMS, canEditEmail, etc.) for each content type you're working with</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Analytics */}
            <div className="doc-section" ref={el => sectionRefs.current['analytics'] = el}>
                <div className="section-title" onClick={() => toggleSection('analytics')} data-section="analytics">
                    {expandedSections.analytics ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Analytics</h2>
                </div>
                {expandedSections.analytics && (
                    <div className="section-content">
                        <h3>Overview</h3>
                        <p>The Analytics dashboard provides comprehensive insights into your content creation and management activities.</p>

                        <h3>Overview Cards</h3>
                        <p>At the top of the dashboard, you'll see key metrics:</p>
                        <ul>
                            <li><strong>Total Creatives:</strong> Total number of creatives in the system</li>
                            <li><strong>Active Users:</strong> Number of users who have created content</li>
                            <li><strong>Languages:</strong> Total active languages in use</li>
                            <li><strong>Avg Versions:</strong> Average number of versions per creative (indicates editing activity)</li>
                        </ul>

                        <h3>Charts & Visualizations</h3>
                        
                        <h4>Creation Trends (Line Chart)</h4>
                        <ul>
                            <li>Shows creative creation activity over time</li>
                            <li>Last 30 days by default</li>
                            <li>Helps identify peak creation periods</li>
                            <li>Filter by date range using date pickers</li>
                        </ul>

                        <h4>Creatives by Type (Doughnut Chart)</h4>
                        <ul>
                            <li>Distribution of content formats (Email, SMS, Popup, Push Notification)</li>
                            <li>Shows percentage and count for each type</li>
                            <li>Helps understand content mix</li>
                        </ul>

                        <h4>Top Creators (Bar Chart)</h4>
                        <ul>
                            <li>Top 10 users by number of creatives created</li>
                            <li>Horizontal bar chart for easy comparison</li>
                            <li>Shows total count per user</li>
                        </ul>

                        <h4>Top Editors (Bar Chart)</h4>
                        <ul>
                            <li>Top 10 users by number of edits made</li>
                            <li>Counts total versions created (excluding initial creation)</li>
                            <li>Identifies most active editors</li>
                        </ul>

                        <h4>Language Usage (Bar Chart)</h4>
                        <ul>
                            <li>Shows how many creatives use each language</li>
                            <li>Helps identify most/least used languages</li>
                            <li>Useful for resource planning</li>
                        </ul>

                        <h4>Status Distribution (Bar Chart)</h4>
                        <ul>
                            <li>Shows creatives grouped by status</li>
                            <li>Only visible when status system is enabled</li>
                            <li>Helps track workflow progress</li>
                        </ul>

                        <h3>Date Filtering</h3>
                        <p>Use the date range filters to analyze specific time periods:</p>
                        <ul>
                            <li>Select "From Date" and "To Date"</li>
                            <li>Click "Apply Filters" to update all charts</li>
                            <li>Default shows last 30 days</li>
                            <li>All charts update simultaneously with the filter</li>
                        </ul>

                        <h3>Export Options</h3>
                        <p>Export your data for external analysis:</p>
                        <ul>
                            <li><strong>Export as CSV:</strong> Download data in Excel-compatible format</li>
                            <li><strong>Export as JSON:</strong> Download raw data for programmatic use</li>
                            <li>Exports include all analytics data respecting current date filters</li>
                            <li><strong>Permission Required:</strong> canExportReports</li>
                        </ul>

                        <h3>User Performance Details</h3>
                        <p>Click on a user in the charts to view detailed statistics:</p>
                        <ul>
                            <li>Total creatives created</li>
                            <li>Total edits made</li>
                            <li>Breakdown by content type</li>
                            <li>Recent activity timeline</li>
                        </ul>

                        <h3>Permissions</h3>
                        <ul>
                            <li><strong>canViewAnalytics:</strong> Required to access the Analytics page</li>
                            <li><strong>canExportReports:</strong> Required to export data to CSV/JSON</li>
                            <li>Admin users have full access to all analytics features</li>
                        </ul>

                        <h3>Best Practices</h3>
                        <ul>
                            <li>Review analytics weekly to identify trends</li>
                            <li>Use date filters to analyze specific campaigns or periods</li>
                            <li>Export data for presentations or external reporting</li>
                            <li>Monitor Top Creators/Editors to recognize productive team members</li>
                            <li>Check Language Usage to optimize localization efforts</li>
                            <li>Use Status Distribution to identify workflow bottlenecks</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Editing Content */}
            <div className="doc-section" ref={el => sectionRefs.current['editing'] = el}>
                <div className="section-title" onClick={() => toggleSection('editing')} data-section="editing">
                    {expandedSections.editing ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Editing Content</h2>
                </div>
                {expandedSections.editing && (
                    <div className="section-content">
                        <h3>Edit Mode</h3>
                        <p>When editing content, you can:</p>
                        <ul>
                            <li>Modify all content fields</li>
                            <li>Edit content in all available languages</li>
                            <li>Change status (if status system is enabled)</li>
                            <li>Add or remove languages</li>
                            <li>View live preview of changes</li>
                        </ul>

                        <h3>Version Control</h3>
                        <p>Every save creates a new version with:</p>
                        <ul>
                            <li>Timestamp of change</li>
                            <li>User who made the change</li>
                            <li>All modified fields</li>
                            <li>Complete content snapshot</li>
                        </ul>
                        <p>You can view version history and restore previous versions if needed (requires "Restore History" permission).</p>

                        <h3>Multi-Language Editing</h3>
                        <p>Content is stored with all language variations. When editing:</p>
                        <ul>
                            <li>Select the language tab to edit that language's content</li>
                            <li>Each language maintains its own version of all fields</li>
                            <li>You can add new languages or remove existing ones</li>
                        </ul>

                        <h3>Version History with Comments</h3>
                        <p>On the version history page, you can:</p>
                        <ul>
                            <li>View all versions with expandable details</li>
                            <li>See changes made in each version</li>
                            <li>Add comments to versions for collaboration</li>
                            <li>Edit or delete your own comments</li>
                            <li>Restore previous versions (creates a new version)</li>
                            <li>Compare different language versions side-by-side</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Translation */}
            <div className="doc-section" ref={el => sectionRefs.current['translation'] = el}>
                <div className="section-title" onClick={() => toggleSection('translation')} data-section="translation">
                    {expandedSections.translation ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>AI Translation</h2>
                </div>
                {expandedSections.translation && (
                    <div className="section-content">
                        <h3>Automatic Translation</h3>
                        <p>When creating content in "Translate Mode", AI automatically translates your content to all selected languages.</p>

                        <h3>How It Works</h3>
                        <ol>
                            <li>Create content in your primary language</li>
                            <li>Select target languages</li>
                            <li>AI translates all fields including headlines, body, subject lines, etc.</li>
                            <li>Translations maintain HTML formatting where applicable</li>
                            <li>All language versions are saved together</li>
                        </ol>

                        <h3>Supported Content Types</h3>
                        <p>AI translation is available for:</p>
                        <ul>
                            <li>Email (all fields including subject lines and pre-header)</li>
                            <li>SMS</li>
                            <li>Push Notifications</li>
                            <li>Popups</li>
                            <li>Free Text</li>
                        </ul>

                        <h3>Translation Quality</h3>
                        <p>AI translation:</p>
                        <ul>
                            <li>Maintains context and tone</li>
                            <li>Preserves HTML formatting</li>
                            <li>Adapts content to cultural nuances</li>
                            <li>Can be manually edited after generation</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Status Management */}
            <div className="doc-section" ref={el => sectionRefs.current['statuses'] = el}>
                <div className="section-title" onClick={() => toggleSection('statuses')} data-section="statuses">
                    {expandedSections.statuses ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Status Management</h2>
                </div>
                {expandedSections.statuses && (
                    <div className="section-content">
                        <h3>Status System</h3>
                        <p>The status system is <strong>optional and configurable</strong>. Users decide whether to use it and which statuses to enable.</p>

                        <h3>Enable/Disable Status System</h3>
                        <p>Navigate to <strong>Status Management</strong> to:</p>
                        <ul>
                            <li>Toggle the status system on/off with a single switch</li>
                            <li>Create custom status names with color coding</li>
                            <li>Reorder statuses by dragging them</li>
                            <li>Edit or delete existing statuses</li>
                        </ul>
                        <p><strong>Important:</strong> You need at least 2 statuses created before you can enable the status system.</p>

                        <h3>When Status System is Enabled</h3>
                        <p>If enabled, all content creation/editing forms include a status dropdown to assign workflow states to content.</p>

                        <h3>When Status System is Disabled</h3>
                        <p>Status fields are hidden throughout the application - no status assignment or filtering is available.</p>

                        <h3>Status Features</h3>
                        <ul>
                            <li><strong>Custom Names:</strong> Define status labels that match your workflow (e.g., "Draft", "In Review", "Approved", "Published")</li>
                            <li><strong>Color Coding:</strong> Assign colors to visually differentiate statuses</li>
                            <li><strong>Ordering:</strong> Control the order statuses appear in dropdowns</li>
                            <li><strong>Filtering:</strong> Filter library content by status (when system is enabled)</li>
                            <li><strong>Dashboard Stats:</strong> See content distribution across statuses (when system is enabled)</li>
                        </ul>

                        <h3>Best Practices</h3>
                        <ul>
                            <li>Design statuses that match your approval workflow</li>
                            <li>Use distinct colors for easy visual recognition</li>
                            <li>Order statuses logically (e.g., Draft → Review → Approved → Published)</li>
                            <li>Only enable the system when you have a defined workflow process</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Email Templates */}
            <div className="doc-section" ref={el => sectionRefs.current['emailTemplates'] = el}>
                <div className="section-title" onClick={() => toggleSection('emailTemplates')} data-section="emailTemplates">
                    {expandedSections.emailTemplates ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Email Templates</h2>
                </div>
                {expandedSections.emailTemplates && (
                    <div className="section-content">
                        <h3>Email Template Builder</h3>
                        <p>Build reusable email templates from blocks. Go to <strong>Template Builder → Create Template</strong>. Saved templates appear as an extra <strong>Template Version</strong> when viewing an email creative.</p>
                        <p><strong>Permission Required:</strong> "Create/Edit/Delete Email Templates" permissions control access to this feature.</p>

                        <h3>How Templates Are Built</h3>
                        <p>A template is a 600px wide email table. Every block is one table row (<code>&lt;tr&gt;</code>), so the layout stays stable in all email clients (Outlook, Gmail, Apple Mail).</p>
                        <p>The builder has three columns:</p>
                        <ul>
                            <li><strong>Blocks (left):</strong> predefined blocks, your saved blocks ("My blocks") and the "Custom block" button</li>
                            <li><strong>Template (middle):</strong> the blocks of the template in order</li>
                            <li><strong>Preview (right):</strong> live preview with the brand selected at the top, with desktop and mobile width</li>
                        </ul>

                        <h3>Adding and Arranging Blocks</h3>
                        <ol>
                            <li>Drag a block from the left into the template (an orange line shows where it will be placed), or click it to add it at the end</li>
                            <li>Reorder blocks by dragging them, or with the ↑ / ↓ buttons</li>
                            <li>Use the block buttons to edit its HTML, duplicate or remove it</li>
                            <li>Enter a template name and click <strong>Save template</strong></li>
                        </ol>

                        <h4>Predefined Blocks:</h4>
                        <ul>
                            <li>View in browser, Header – Logo, Header – Logo + Button</li>
                            <li>Hero image, Headline, Body text, Button, Text under button</li>
                            <li>Image + Text (2 columns), Additional text</li>
                            <li>Divider, Spacer, Marketing disclaimer, Footer (small disclaimer + unsubscribe link)</li>
                        </ul>

                        <h3>Brand Settings</h3>
                        <p>Blocks use the settings of the brand instead of fixed values: logo, logo link, background and frame colors, button colors, button border radius, disclaimers, view in browser and unsubscribe URLs. Change the brand at the top of the builder to preview the template with another brand.</p>
                        <p>Text color is chosen automatically: light text on dark brand backgrounds and dark text on light ones.</p>

                        <h3>Custom Blocks</h3>
                        <p>Click <strong>Custom block</strong> to write your own HTML. To keep the template from breaking, a block must:</p>
                        <ul>
                            <li>Start with <code>&lt;tr&gt;</code> and end with <code>&lt;/tr&gt;</code> (several rows one after another are allowed)</li>
                            <li>Close every <code>&lt;table&gt;</code>, <code>&lt;tr&gt;</code> and <code>&lt;td&gt;</code> it opens</li>
                            <li>Contain at least one <code>&lt;td&gt;</code></li>
                            <li>Not contain text outside the rows, or <code>&lt;html&gt;</code>, <code>&lt;body&gt;</code>, <code>&lt;head&gt;</code>, <code>&lt;style&gt;</code>, <code>&lt;script&gt;</code></li>
                        </ul>
                        <p>The editor checks the HTML while you type and shows the problem (for example "The block must start with &lt;tr&gt;."). A block cannot be added until it is valid, and the server checks it again when the template is saved.</p>
                        <p>Tick <strong>Also save to My blocks</strong> to reuse the block in other templates. My blocks are shared with everyone who can build templates.</p>

                        <h4>Example Custom Block:</h4>
                        <pre><code>{`<tr>
  <td style="padding:20px 30px;font-family:Arial,sans-serif;color:{{brand.textColor}};">
    {{headline}}
  </td>
</tr>`}</code></pre>

                        <h3>Template Placeholders</h3>
                        <p>Insert them with the buttons under the HTML editor. They are replaced when the email is generated.</p>
                        <ul>
                            <li><strong>Brand:</strong> <code>{'{{brand.name}}'}</code>, <code>{'{{brand.logo}}'}</code>, <code>{'{{brand.logoLink}}'}</code>, <code>{'{{brand.backgroundColor}}'}</code>, <code>{'{{brand.frameColor}}'}</code>, <code>{'{{brand.textColor}}'}</code>, <code>{'{{brand.mutedTextColor}}'}</code>, <code>{'{{brand.buttonColor}}'}</code>, <code>{'{{brand.buttonTextColor}}'}</code>, <code>{'{{brand.borderRadius}}'}</code>, <code>{'{{brand.viewInBrowserURL}}'}</code>, <code>{'{{brand.unsubscribeURL}}'}</code>, <code>{'{{brand.disclaimer}}'}</code>, <code>{'{{brand.smallDisclaimer}}'}</code></li>
                            <li><strong>Creative content:</strong> <code>{'{{headline}}'}</code>, <code>{'{{body}}'}</code>, <code>{'{{bodyImage}}'}</code>, <code>{'{{bodyImageLink}}'}</code>, <code>{'{{bodyBtnName}}'}</code>, <code>{'{{bodyBtnLink}}'}</code>, <code>{'{{additionalText}}'}</code>, <code>{'{{additionalTextUnderBTN}}'}</code>, <code>{'{{preHeader}}'}</code>, <code>{'{{subjectLine}}'}</code></li>
                        </ul>

                        <h3>Using a Template</h3>
                        <p>Open an email creative from the Library and choose the template under <strong>Template Version</strong>. The content of the selected language and the selected brand are applied. "Copy HTML" and "Push to Optimove" use the same HTML.</p>
                        <p>Templates created with the previous row/column builder keep opening in that builder and keep working as before.</p>
                    </div>
                )}
            </div>

            {/* CRM Integrations */}
            <div className="doc-section" ref={el => sectionRefs.current['integrations'] = el}>
                <div className="section-title" onClick={() => toggleSection('integrations')} data-section="integrations">
                    {expandedSections.integrations ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>CRM Integrations (Optimove)</h2>
                </div>
                {expandedSections.integrations && (
                    <div className="section-content">
                        <h3>Overview</h3>
                        <p>Email creatives can be sent to a CRM as templates. Optimove (OptiMail) is supported today; other CRMs will be added to the same page. Only one CRM can be active at a time.</p>
                        <p><strong>Permissions Required:</strong> "Manage Integrations" to connect and configure a CRM, "Push Templates to CRM" to send creatives. Admins have both.</p>

                        <h3>Connecting Optimove</h3>
                        <ol>
                            <li>Go to <strong>Settings → Integrations</strong></li>
                            <li>On the Optimove card click <strong>Connect</strong> (or <strong>Configure</strong>)</li>
                            <li>Paste the <strong>API Key</strong> from Optimove</li>
                            <li>Enter the <strong>API Base URL</strong> of your Optimove account (for example <code>https://api5.optimove.net</code> - Optimove tells each account which host to use)</li>
                            <li>Click <strong>Save &amp; Test</strong> - the key is checked and the OptiMail brands are loaded</li>
                            <li>Click <strong>Activate</strong> - the card shows "Active" and the top of the page shows "Active CRM: Optimove"</li>
                        </ol>
                        <p>The API key is stored encrypted and is never shown again (only the last 4 characters). Leave the field empty when editing to keep the saved key; paste a new one to replace it. <strong>Deactivate</strong> stops pushing without deleting the settings; the trash icon removes the configuration.</p>

                        <h3>Optimove Brands</h3>
                        <p>The OptiMail brands are loaded from Optimove automatically - only brands that have email sender addresses are listed. Use <strong>Refresh</strong> in the Configure window (or ↻ next to the brand list when pushing) after adding a brand in Optimove.</p>

                        <h3>Pushing a Creative</h3>
                        <ol>
                            <li>Open an email creative from the Library</li>
                            <li>Select the <strong>Brand</strong>, <strong>Language</strong> and <strong>Template Version</strong> - exactly the HTML in the preview is sent</li>
                            <li>Click <strong>Push to Optimove</strong> (top right)</li>
                            <li>Check the template name, subject, Optimove brand and folder</li>
                            <li>Click <strong>Push</strong> - the Optimove template number is shown (for example "Template #9481 created in Optimove")</li>
                        </ol>

                        <h4>Optimove Brand Selection:</h4>
                        <ul>
                            <li>If an Optimove brand has the same name as the Content Craft brand, it is selected automatically ("matched by brand name"). Case, spaces and symbols are ignored.</li>
                            <li>If several Optimove brands share that name (for example a QA copy), the main one - the one with the most folders - is selected</li>
                            <li>If there is no match, choose the brand from the list. Use <strong>Change</strong> to pick a different brand than the automatic one</li>
                        </ul>

                        <h4>Folder:</h4>
                        <p>After the brand is selected its OptiMail folders are listed with their full path (for example "Newsletters / 07. July Newsletters 2026"). Without a folder the template goes to the brand's root folder.</p>

                        <h4>Filled in Automatically:</h4>
                        <p>From email, Reply-To and From name are taken from the selected Optimove brand (an "info@" address is preferred over "no-reply@"; the From name is the brand name).</p>

                        <h3>Creating vs. Updating</h3>
                        <ul>
                            <li>The first push of a creative (per language and brand) <strong>creates</strong> a new template</li>
                            <li>Later pushes <strong>update</strong> the same template, so no duplicates are created in Optimove</li>
                            <li>"Create a new template instead" makes a new template; "Update another existing Optimove template" overwrites a template you choose from the Optimove list</li>
                        </ul>

                        <h3>All Languages at Once</h3>
                        <p>Tick <strong>All languages</strong> to push one template per language in one go. The language code is added to the template name, every language uses its own translation of the chosen subject line (Subject Line 1, 2 or 3 - falls back to Subject Line 1 if empty), and the table shows the result for each language. If one language fails the others continue; pushing again only updates the ones already created.</p>
                    </div>
                )}
            </div>

            {/* Placeholders */}
            <div className="doc-section" ref={el => sectionRefs.current['placeholders'] = el}>
                <div className="section-title" onClick={() => toggleSection('placeholders')} data-section="placeholders">
                    {expandedSections.placeholders ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Placeholders</h2>
                </div>
                {expandedSections.placeholders && (
                    <div className="section-content">
                        <h3>What are Placeholders?</h3>
                        <p>Placeholders are dynamic variables that can be inserted into your content to personalize messages. They are replaced with actual values when the content is sent or displayed.</p>
                        
                        <h3>Creating Placeholders</h3>
                        <p>Navigate to <strong>Settings → Placeholders</strong> to manage placeholders.</p>
                        <p><strong>Permission Required:</strong> "Create/Edit/Delete Placeholders" permissions control access to this feature.</p>
                        
                        <h4>Required Fields:</h4>
                        <ul>
                            <li><strong>Title:</strong> A descriptive name for the placeholder (e.g., "First Name", "Email Address")</li>
                            <li><strong>Placeholder:</strong> The actual variable text that will be inserted (e.g., First_Name, %FIRSTNAME%)</li>
                        </ul>

                        <h3>Placeholder Format</h3>
                        <p>You can use any format you prefer for placeholders. Common formats include:</p>
                        <ul>
                            <li><code>First_Name</code> - Simple text</li>
                            <li>Double curly braces (common in template engines)</li>
                            <li><code>%FIRSTNAME%</code> - Percentage symbols</li>
                            <li><code>[First_Name]</code> - Square brackets</li>
                            <li>Any custom format your system uses</li>
                        </ul>

                        <h3>Using Placeholders</h3>
                        <p>When creating content (SMS, Email, Popup, Push Notification):</p>
                        <ol>
                            <li>Click on any text field where you want to insert a placeholder</li>
                            <li>Click the <strong>"Placeholders"</strong> dropdown button next to the field</li>
                            <li>Select the placeholder you want to use from the list</li>
                            <li>The placeholder will be copied to your clipboard</li>
                            <li>Paste it (Ctrl+V) wherever you want in the text field</li>
                        </ol>

                        <h3>Where Can You Use Placeholders?</h3>
                        <p>Placeholders can be inserted into:</p>
                        <ul>
                            <li><strong>SMS:</strong> Message body</li>
                            <li><strong>Email:</strong> Subject lines, pre-header, headline, email body, additional text, text under button</li>
                            <li><strong>Popup:</strong> Headline and content</li>
                            <li><strong>Push Notification:</strong> Headline and message body</li>
                        </ul>
                        <p><em>Note: Placeholders are NOT available in title fields, button text, or link/URL fields.</em></p>

                        <h3>Common Use Cases</h3>
                        <ul>
                            <li>Personalizing greetings with user names</li>
                            <li>Including user-specific information like order IDs</li>
                            <li>Dynamic dates and times for appointments</li>
                            <li>Account information like balances</li>
                        </ul>

                        <h3>Managing Placeholders</h3>
                        <ul>
                            <li><strong>View:</strong> See all available placeholders in the list</li>
                            <li><strong>Create:</strong> Add new placeholders as needed</li>
                            <li><strong>Edit:</strong> Update placeholder title or value</li>
                            <li><strong>Delete:</strong> Remove placeholders that are no longer needed</li>
                        </ul>

                        <h3>Best Practices</h3>
                        <ul>
                            <li>Use clear, descriptive titles so team members can easily find the right placeholder</li>
                            <li>Be consistent with your placeholder format across all your content</li>
                            <li>Document what each placeholder represents, especially for complex variables</li>
                            <li>Test your content with sample data to ensure placeholders work correctly</li>
                            <li>Don't delete placeholders that are actively used in existing content</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* User Management */}
            <div className="doc-section" ref={el => sectionRefs.current['users'] = el}>
                <div className="section-title" onClick={() => toggleSection('users')} data-section="users">
                    {expandedSections.users ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>User Management</h2>
                </div>
                {expandedSections.users && (
                    <div className="section-content">
                        <h3>Creating Users</h3>
                        <p>Navigate to <strong>User Management</strong> to create new users. Required fields:</p>
                        <ul>
                            <li><strong>Username:</strong> Unique login identifier</li>
                            <li><strong>First Name:</strong> User's first name</li>
                            <li><strong>Last Name:</strong> User's last name</li>
                            <li><strong>Email:</strong> User's email address</li>
                            <li><strong>Password:</strong> Minimum 6 characters</li>
                            <li><strong>Role:</strong> Assign a role (Admin or custom role)</li>
                        </ul>

                        <h3>Editing Users</h3>
                        <p>You can modify:</p>
                        <ul>
                            <li>First and last name</li>
                            <li>Email address</li>
                            <li>Assigned role</li>
                            <li>Password (if needed)</li>
                        </ul>

                        <h3>Deleting Users</h3>
                        <p>When a user is deleted:</p>
                        <ul>
                            <li>The user account is removed from the system</li>
                            <li>Content created by that user remains in the library</li>
                            <li>The username is still visible in "Created By" and "Last Updated By" fields</li>
                            <li>Deleted users appear in library filters to maintain audit trail</li>
                        </ul>

                        <h3>User List</h3>
                        <p>The user list shows:</p>
                        <ul>
                            <li>Username and full name</li>
                            <li>Email address</li>
                            <li>Assigned role</li>
                            <li>Last login date/time</li>
                            <li>Actions (Edit/Delete)</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Brands */}
            <div className="doc-section" ref={el => sectionRefs.current['brands'] = el}>
                <div className="section-title" onClick={() => toggleSection('brands')} data-section="brands">
                    {expandedSections.brands ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Brand Management</h2>
                </div>
                {expandedSections.brands && (
                    <div className="section-content">
                        <h3>Brand Configuration</h3>
                        <p>Brands store styling and configuration for emails and other content types.</p>
                        <p><strong>Permission Required:</strong> "Manage Brands" and "Delete Brands" permissions control access.</p>

                        <h3>Brand Fields</h3>
                        <ul>
                            <li><strong>Brand Name:</strong> Identifier for the brand</li>
                            <li><strong>Logo:</strong> Brand logo image URL</li>
                            <li><strong>Colors:</strong> Frame color, background color, button color, button text color</li>
                            <li><strong>Border Radius:</strong> Rounded corners of buttons, as a whole number of pixels (for example <code>5</code>, without "px")</li>
                            <li><strong>Disclaimers:</strong> Marketing disclaimers and legal text</li>
                            <li><strong>URLs:</strong> Unsubscribe URL, view in browser URL</li>
                            <li><strong>Language-Specific Settings:</strong> Different logo links per language</li>
                        </ul>

                        <h3>Multi-Language Brand Support</h3>
                        <p>Brands can have language-specific configurations, allowing different logos or URLs for each active language.</p>
                    </div>
                )}
            </div>

            {/* Language Management */}
            <div className="doc-section" ref={el => sectionRefs.current['languages'] = el}>
                <div className="section-title" onClick={() => toggleSection('languages')} data-section="languages">
                    {expandedSections.languages ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Language Management</h2>
                </div>
                {expandedSections.languages && (
                    <div className="section-content">
                        <h3>Adding Languages</h3>
                        <p>Navigate to <strong>Language Management</strong> to add languages to the system.</p>
                        <p><strong>Permission Required:</strong> "Manage Languages" permission is needed.</p>

                        <h3>Language Fields</h3>
                        <ul>
                            <li><strong>Language Name:</strong> Full name (e.g., "English", "Български", "Deutsch")</li>
                            <li><strong>Code:</strong> Language code (e.g., "en", "bg", "de")</li>
                            <li><strong>Status:</strong> Active or Inactive</li>
                        </ul>

                        <h3>Active vs Inactive Languages</h3>
                        <ul>
                            <li><strong>Active:</strong> Available for content creation and appears in language selection dropdowns</li>
                            <li><strong>Inactive:</strong> Hidden from dropdowns but existing content in that language is preserved</li>
                        </ul>

                        <h3>Language in Content</h3>
                        <p>Only active languages are available when creating or editing content. Existing content in inactive languages remains accessible but cannot be assigned to new content.</p>
                    </div>
                )}
            </div>

            {/* Profile Settings */}
            <div className="doc-section" ref={el => sectionRefs.current['profile'] = el}>
                <div className="section-title" onClick={() => toggleSection('profile')} data-section="profile">
                    {expandedSections.profile ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Profile Settings</h2>
                </div>
                {expandedSections.profile && (
                    <div className="section-content">
                        <h3>Your Profile</h3>
                        <p>Access your profile from the header to view and edit your personal information.</p>

                        <h3>Profile Information</h3>
                        <p>Your profile displays:</p>
                        <ul>
                            <li><strong>Avatar:</strong> Automatically generated from your initials (First Name + Last Name)</li>
                            <li><strong>Username:</strong> Your login username (cannot be changed)</li>
                            <li><strong>Email:</strong> Your email address (cannot be changed)</li>
                            <li><strong>Role:</strong> Your assigned role and permissions</li>
                            <li><strong>Last Login:</strong> When you last logged into the system</li>
                        </ul>

                        <h3>Edit Names</h3>
                        <p>You can update your first and last name:</p>
                        <ul>
                            <li>Click "Edit Names" in the profile page</li>
                            <li>Modify First Name and Last Name</li>
                            <li>Click "Save Changes"</li>
                            <li>Your avatar initials will update automatically</li>
                        </ul>

                        <h3>Change Password</h3>
                        <p>To change your password:</p>
                        <ol>
                            <li>Click "Change Password"</li>
                            <li>Enter your <strong>Current Password</strong></li>
                            <li>Enter your <strong>New Password</strong> (minimum 6 characters)</li>
                            <li>Re-enter the new password in <strong>Confirm New Password</strong></li>
                            <li>Click "Update Password"</li>
                        </ol>

                        <h3>Password Requirements</h3>
                        <ul>
                            <li>Minimum 6 characters</li>
                            <li>New password must match confirmation</li>
                            <li>Must provide correct current password</li>
                        </ul>

                        <h3>Profile Statistics</h3>
                        <p>Your profile shows statistics about your activity:</p>
                        <ul>
                            <li>Total content created</li>
                            <li>Recent edits</li>
                            <li>Account creation date</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Dark Mode */}
            <div className="doc-section" ref={el => sectionRefs.current['darkMode'] = el}>
                <div className="section-title" onClick={() => toggleSection('darkMode')} data-section="darkMode">
                    {expandedSections.darkMode ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Dark Mode</h2>
                </div>
                {expandedSections.darkMode && (
                    <div className="section-content">
                        <h3>Theme Toggle</h3>
                        <p>Content Craft supports both light and dark themes. Click the sun/moon icon in the header to toggle between modes.</p>

                        <h3>Dark Mode Features</h3>
                        <ul>
                            <li>Reduced eye strain in low-light environments</li>
                            <li>Consistent dark styling across all pages</li>
                            <li>Preserved readability with optimized contrast</li>
                            <li>Theme preference saved per user session</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Best Practices */}
            <div className="doc-section" ref={el => sectionRefs.current['bestPractices'] = el}>
                <div className="section-title" onClick={() => toggleSection('bestPractices')} data-section="bestPractices">
                    {expandedSections.bestPractices ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Best Practices</h2>
                </div>
                {expandedSections.bestPractices && (
                    <div className="section-content">
                        <h3>Content Creation</h3>
                        <ul>
                            <li>Use descriptive content names for easy identification in the library</li>
                            <li>Select appropriate campaign types to organize content logically</li>
                            <li>Keep SMS messages under 160 characters to avoid multi-part messages</li>
                            <li>Write compelling subject lines (3 variations for A/B testing in emails)</li>
                            <li>Use AI Assistant for initial content generation, then refine manually</li>
                            <li>Test content in preview mode before finalizing</li>
                        </ul>

                        <h3>Workflow Management</h3>
                        <ul>
                            <li>Only enable status system if you have a defined approval workflow</li>
                            <li>Create clear, descriptive status names that match your process</li>
                            <li>Use version history to track changes and maintain audit trail</li>
                            <li>Add comments to versions for team collaboration</li>
                            <li>Assign appropriate roles with minimal necessary permissions</li>
                        </ul>

                        <h3>Translation & Localization</h3>
                        <ul>
                            <li>Use AI translation as a starting point, then review for accuracy</li>
                            <li>Consider cultural nuances when translating marketing content</li>
                            <li>Keep active only languages you actively use</li>
                            <li>Test translated content with native speakers when possible</li>
                        </ul>

                        <h3>Security</h3>
                        <ul>
                            <li>Change default admin password immediately</li>
                            <li>Use strong passwords (minimum 6 characters, but longer is better)</li>
                            <li>Assign roles with minimal necessary permissions</li>
                            <li>Regularly review user access and remove inactive users</li>
                            <li>Monitor version history for unauthorized changes</li>
                        </ul>

                        <h3>Performance</h3>
                        <ul>
                            <li>Use library filters to quickly find specific content</li>
                            <li>Organize content with consistent naming conventions</li>
                            <li>Delete unused content to keep library clean</li>
                            <li>Customize dashboard to show only relevant information</li>
                        </ul>
                    </div>
                )}
            </div>

            {/* Troubleshooting */}
            <div className="doc-section" ref={el => sectionRefs.current['troubleshooting'] = el}>
                <div className="section-title" onClick={() => toggleSection('troubleshooting')} data-section="troubleshooting">
                    {expandedSections.troubleshooting ? <FiChevronDown /> : <FiChevronRight />}
                    <h2>Troubleshooting</h2>
                </div>
                {expandedSections.troubleshooting && (
                    <div className="section-content">
                        <h3>Cannot Create Content</h3>
                        <ul>
                            <li>Verify you have the correct permission for that content type</li>
                            <li>Check if required fields are filled (Content Name, Campaign Type, Body, Languages)</li>
                            <li>If status system is enabled, ensure a status is selected</li>
                            <li>Ensure at least one active language exists in the system</li>
                        </ul>

                        <h3>AI Assistant Not Working</h3>
                        <ul>
                            <li>Verify you have "Use AI Assistant" permission</li>
                            <li>Check that OpenAI API key is configured on the backend</li>
                            <li>Ensure all required steps are completed before generation</li>
                            <li>Try refreshing the page if generation seems stuck</li>
                        </ul>

                        <h3>Translation Issues</h3>
                        <ul>
                            <li>Verify you have AI Translation permission for that content type</li>
                            <li>Check that target languages are active in Language Management</li>
                            <li>Ensure content to translate is not empty</li>
                            <li>Review translated content for context accuracy</li>
                        </ul>

                        <h3>Cannot Enable Status System</h3>
                        <ul>
                            <li>You need at least 2 statuses created before enabling the system</li>
                            <li>Go to Status Management and create additional statuses</li>
                            <li>Once you have 2+ statuses, toggle the switch to enable</li>
                        </ul>

                        <h3>Cannot Delete Role</h3>
                        <ul>
                            <li>Admin role is a system role and cannot be deleted</li>
                            <li>Custom roles can only be deleted if no users are assigned to them</li>
                            <li>Reassign users to a different role before deleting</li>
                        </ul>

                        <h3>Missing Content in Library</h3>
                        <ul>
                            <li>Check active filters - you may be filtering out the content</li>
                            <li>Verify you have "View Library" permission</li>
                            <li>Use search to look for specific content names</li>
                            <li>Check if you have permission to view that content type</li>
                        </ul>

                        <h3>"Push to Optimove" Button Is Missing</h3>
                        <ul>
                            <li>The button is shown only for email creatives</li>
                            <li>Optimove must be connected and activated in Settings → Integrations ("Active CRM: Optimove")</li>
                            <li>Your role needs the "Push Templates to CRM" permission</li>
                            <li>Reload the page after activating the integration</li>
                        </ul>

                        <h3>Optimove Errors</h3>
                        <ul>
                            <li><strong>"Invalid API key or missing permissions":</strong> check the API key and the API Base URL in Settings → Integrations</li>
                            <li><strong>No brands in the list:</strong> click Refresh / ↻; only OptiMail brands with sender addresses are shown</li>
                            <li><strong>"has no From email address configured":</strong> add a sender address to that brand in Optimove</li>
                            <li><strong>Template not found when updating:</strong> the template was deleted in Optimove - choose "Create a new template instead"</li>
                        </ul>

                        <h3>Custom Block Is Rejected</h3>
                        <ul>
                            <li>The HTML must start with &lt;tr&gt; and end with &lt;/tr&gt;</li>
                            <li>Every &lt;table&gt;, &lt;tr&gt; and &lt;td&gt; must be closed in the right order</li>
                            <li>Move styles inline (style="...") - &lt;style&gt; and &lt;script&gt; tags are not allowed in blocks</li>
                        </ul>

                        <h3>Cannot See User in Created By Filter</h3>
                        <ul>
                            <li>Created By filter only shows users who created content, not all system users</li>
                            <li>Deleted users still appear if they created content (username shown)</li>
                            <li>If no content was created by a user, they won't appear in the filter</li>
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Documentation;
